import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useAuth } from '../module_roots/AuthContext';
// import "../styles/EmployeeSalary.css";
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
    faMoneyBillWave,
    faUser,
    faCalendar,
    faSave,
    faTimes,
    faSpinner,
    faCheckCircle,
    faExclamationTriangle,
    faStore,
    faExchangeAlt,
    faSearch,
    faClock,
    faPercentage,
    faPlusCircle,
    faFileInvoice,
    faPrint,
    faDownload,
    faBuilding,
    faMapMarkerAlt,
    faPhone,
    faEnvelope
} from '@fortawesome/free-solid-svg-icons';
import jsPDF from 'jspdf';
import html2canvas from 'html2canvas';

// Toast Notification Component (same as before)
const ToastNotification = ({ toast, setToast, translations }) => {
    const getIconForType = (type) => {
        switch (type) {
            case 'success':
                return faCheckCircle;
            case 'error':
                return faExclamationTriangle;
            case 'warning':
                return faExclamationTriangle;
            default:
                return faCheckCircle;
        }
    };

    const getTitleForType = (type) => {
        switch (type) {
            case 'success':
                return 'Success';
            case 'error':
                return 'Error';
            case 'warning':
                return 'Warning';
            default:
                return '';
        }
    };

    if (!toast.show) return null;

    const opacity = Math.max(toast.progress / 100, 0.1);

    return (
        <div
            className={`toast-notification ${toast.type} ${toast.direction === 'rtl' ? 'toast-rtl' : 'toast-ltr'}`}
            style={{ opacity }}
        >
            <div className="toast-content">
                <div className="toast-icon">
                    <FontAwesomeIcon icon={getIconForType(toast.type)} />
                </div>
                <div className="toast-text">
                    <div className="toast-title">
                        {getTitleForType(toast.type)}
                    </div>
                    <div className="toast-message">
                        {toast.message}
                    </div>
                </div>
                <button
                    className="toast-close"
                    onClick={() => setToast(prev => ({ ...prev, show: false }))}
                >
                    <FontAwesomeIcon icon={faTimes} />
                </button>
            </div>
            <div className="toast-timer">
                <div
                    className="toast-progress-bar"
                    style={{
                        width: `${toast.progress}%`,
                        transition: 'width 0.1s linear'
                    }}
                ></div>
                <div className="toast-countdown">
                    {toast.timeLeft}s
                </div>
            </div>
        </div>
    );
};

// Shop Selector Modal Component
const ShopSelectorModal = ({ isOpen, onClose, shops, currentShop, onSelectShop, translations, loading }) => {
    if (!isOpen) return null;

    const handleShopSelect = (shop) => {
        onSelectShop(shop);
        onClose();
    };

    return (
        <div className="modal-overlay">
            <div className="modal-content shop-selector-modal">
                <div className="modal-header">
                    <h3>{translations?.switchShop || 'Switch Shop'}</h3>
                    <button className="modal-close" onClick={onClose}>
                        <FontAwesomeIcon icon={faTimes} />
                    </button>
                </div>
                <div className="modal-body">
                    {loading ? (
                        <div className="loading-shops">
                            <div className="loading-spinner-small"></div>
                            <p>{translations?.loadingShops || 'Loading shops...'}</p>
                        </div>
                    ) : shops.length === 0 ? (
                        <div className="no-shops">
                            <p>{translations?.noShops || 'No shops available'}</p>
                        </div>
                    ) : (
                        <div className="shop-list">
                            {shops.map((shop) => (
                                <div
                                    key={shop.shop_id}
                                    className={`shop-item ${currentShop?.shop_id === shop.shop_id ? 'active' : ''}`}
                                    onClick={() => handleShopSelect(shop)}
                                >
                                    <div className="shop-item-icon">
                                        <FontAwesomeIcon icon={faStore} />
                                    </div>
                                    <div className="shop-item-details">
                                        <div className="shop-item-name">{shop.shop_name}</div>
                                        <div className="shop-item-code">{shop.shop_code || `ID: ${shop.shop_id}`}</div>
                                    </div>
                                    {currentShop?.shop_id === shop.shop_id && (
                                        <div className="shop-item-current">
                                            <FontAwesomeIcon icon={faCheckCircle} />
                                            <span>{translations?.current || 'Current'}</span>
                                        </div>
                                    )}
                                </div>
                            ))}
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

// Business Info Fetcher
const useBusinessInfo = (username) => {
    const [businessInfo, setBusinessInfo] = useState({
        businessname: '',
        bussinesslogo: '',
        businessaddress: '',
        fullname: '',
        email: '',
        phonenumber: ''
    });
    const [loading, setLoading] = useState(false);

    const fetchBusinessInfo = useCallback(async () => {
        if (!username) return;

        setLoading(true);
        try {
            const response = await fetch('https://ourzestapp.online/oz_backend/get_business_info.php', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    action: 'get_business_info',
                    username: username
                })
            });

            const data = await response.json();
            if (data.status === 'success') {
                setBusinessInfo({
                    businessname: data.businessname || '',
                    bussinesslogo: data.bussinesslogo || '',
                    businessaddress: data.businessaddress || '',
                    fullname: data.fullname || '',
                    email: data.email || '',
                    phonenumber: data.phonenumber || ''
                });
            }
        } catch (error) {
            console.error('Error fetching business info:', error);
        } finally {
            setLoading(false);
        }
    }, [username]);

    useEffect(() => {
        fetchBusinessInfo();
    }, [fetchBusinessInfo]);

    return { businessInfo, loading, refetch: fetchBusinessInfo };
};

const AddSalary = () => {
    const { user, currentShop, switchShop, getShopsForSwitch } = useAuth();
    const { businessInfo } = useBusinessInfo(user?.username);

    const [formData, setFormData] = useState({
        employeeid: '',
        employee_name: '',
        salary_month: new Date().toISOString().slice(0, 7), // YYYY-MM format
        basic_salary: 0,
        allowances: 0,
        deductions: 0,
        bonus: 0,
        overtime_hours: 0,
        overtime_amount: 0,
        commission_amount: 0,
        net_salary: 0,
        payment_method: 'cash',
        payment_status: 'pending',
        payment_date: new Date().toISOString().split('T')[0],
        transaction_id: '',
        notes: '',
        themeColor: localStorage.getItem('ourzest_theme') || '#6366f1'
    });

    const [errors, setErrors] = useState({});
    const [loading, setLoading] = useState(false);
    const [employees, setEmployees] = useState([]);
    const [employeeSearchTerm, setEmployeeSearchTerm] = useState('');
    const [showEmployeeDropdown, setShowEmployeeDropdown] = useState(false);
    const [selectedEmployee, setSelectedEmployee] = useState(null);
    const [employeeDetails, setEmployeeDetails] = useState(null);

    // Toast state
    const [toast, setToast] = useState({
        show: false,
        type: '',
        message: '',
        direction: 'ltr',
        progress: 100,
        timeLeft: 3
    });

    // Shop selector state
    const [showShopSelector, setShowShopSelector] = useState(false);
    const [availableShops, setAvailableShops] = useState([]);
    const [loadingShops, setLoadingShops] = useState(false);

    // Show pay voucher modal
    const [showPayVoucher, setShowPayVoucher] = useState(false);
    const [voucherData, setVoucherData] = useState(null);

    // Refs
    const isSubmittingRef = useRef(false);
    const submissionLockRef = useRef(false);
    const lastSubmissionRef = useRef(null);
    const toastTimerRef = useRef(null);
    const formRef = useRef(null);
    const employeeDropdownRef = useRef(null);

    const isRTL = localStorage.getItem('ourzest_language') === 'ur' || localStorage.getItem('ourzest_language') === 'ar';

    // Payment method options
    const paymentMethodOptions = [
        { value: 'cash', label: 'Cash' },
        { value: 'bank_transfer', label: 'Bank Transfer' },
        { value: 'cheque', label: 'Cheque' }
    ];

    // Payment status options
    const paymentStatusOptions = [
        { value: 'pending', label: 'Pending' },
        { value: 'paid', label: 'Paid' },
        { value: 'cancelled', label: 'Cancelled' }
    ];

    // Function to show toast
    const showToast = (type, message) => {
        if (toastTimerRef.current) {
            clearInterval(toastTimerRef.current);
        }

        const isRTL = localStorage.getItem('ourzest_language') === 'ur' || localStorage.getItem('ourzest_language') === 'ar';
        const direction = isRTL ? 'rtl' : 'ltr';

        setToast({
            show: true,
            type,
            message,
            direction,
            progress: 100,
            timeLeft: 3
        });

        let timeLeft = 3;
        const timer = setInterval(() => {
            timeLeft -= 0.1;
            const progress = (timeLeft / 3) * 100;

            setToast(prev => ({
                ...prev,
                progress,
                timeLeft: parseFloat(timeLeft.toFixed(1))
            }));

            if (timeLeft <= 0) {
                clearInterval(timer);
                setToast(prev => ({ ...prev, show: false }));
            }
        }, 100);

        toastTimerRef.current = timer;
    };

    // Clean up timers
    useEffect(() => {
        return () => {
            if (toastTimerRef.current) {
                clearInterval(toastTimerRef.current);
            }
            isSubmittingRef.current = false;
            submissionLockRef.current = false;
        };
    }, []);

    // Fetch employees for dropdown
    const fetchEmployees = async () => {
        if (!user || !user.username || !currentShop) return;

        try {
            const response = await fetch('https://ourzestapp.online/oz_backend/employees.php', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    action: 'get_employee_records',
                    username: user.username,
                    shop_id: currentShop.shop_id
                })
            });

            const data = await response.json();
            if (data.status === 'success') {
                setEmployees(data.data || []);
            }
        } catch (error) {
            console.error('Error fetching employees:', error);
        }
    };

    useEffect(() => {
        if (currentShop) {
            fetchEmployees();
        }
    }, [currentShop]);

    // Handle click outside employee dropdown
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (employeeDropdownRef.current && !employeeDropdownRef.current.contains(event.target)) {
                setShowEmployeeDropdown(false);
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, []);

    // Filter employees based on search term
    const filteredEmployees = employeeSearchTerm.trim() === ''
        ? employees
        : employees.filter(emp =>
            emp.employee_name?.toLowerCase().includes(employeeSearchTerm.toLowerCase()) ||
            emp.employeeid?.toString().includes(employeeSearchTerm) ||
            emp.phone?.includes(employeeSearchTerm)
        );

    // Handle employee selection
    const selectEmployee = (employee) => {
        setSelectedEmployee(employee);
        setFormData(prev => ({
            ...prev,
            employeeid: employee.employeeid,
            employee_name: employee.employee_name
        }));
        setEmployeeSearchTerm(employee.employee_name);
        setShowEmployeeDropdown(false);
        setEmployeeDetails(employee);

        // Auto-calculate net salary based on employee's salary type
        calculateNetSalary({
            ...formData,
            employeeid: employee.employeeid,
            employee_name: employee.employee_name
        }, employee);
    };

    // Calculate net salary
    const calculateNetSalary = (data, employee = employeeDetails) => {
        if (!employee) return;

        let netSalary = 0;

        if (employee.salary_type === 'fixed') {
            netSalary = parseFloat(data.basic_salary || employee.base_salary || 0) +
                parseFloat(data.allowances || 0) +
                parseFloat(data.bonus || 0) +
                parseFloat(data.commission_amount || 0) -
                parseFloat(data.deductions || 0);
        } else if (employee.salary_type === 'hourly') {
            const hourlyRate = parseFloat(employee.hourly_rate || 0);
            const overtimeHours = parseFloat(data.overtime_hours || 0);
            const overtimeAmount = overtimeHours * hourlyRate * 1.5; // 1.5x for overtime
            netSalary = (hourlyRate * 8 * 22) + overtimeAmount; // Assuming 8 hours/day, 22 days/month
        } else if (employee.salary_type === 'commission') {
            netSalary = parseFloat(data.commission_amount || 0);
        }

        setFormData(prev => ({
            ...prev,
            net_salary: netSalary.toFixed(2)
        }));
    };

    // Handle input changes
    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => {
            const newData = { ...prev, [name]: value };

            // Recalculate net salary when relevant fields change
            if (['basic_salary', 'allowances', 'deductions', 'bonus', 'overtime_hours', 'commission_amount'].includes(name)) {
                calculateNetSalary(newData);
            }

            return newData;
        });

        if (errors[name]) {
            setErrors(prev => ({ ...prev, [name]: '' }));
        }
    };

    // Validate form
    const validateForm = () => {
        const newErrors = {};

        if (!formData.employeeid) {
            newErrors.employee = 'Please select an employee';
        }

        if (!formData.salary_month) {
            newErrors.salary_month = 'Please select a salary month';
        }

        if (formData.net_salary <= 0) {
            newErrors.net_salary = 'Net salary must be greater than 0';
        }

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    // Handle form submit
    const handleSubmit = async (e) => {
        e.preventDefault();

        if (!currentShop) {
            showToast('error', 'Please select a shop first');
            setShowShopSelector(true);
            return;
        }

        if (!validateForm()) {
            return;
        }

        if (isSubmittingRef.current || submissionLockRef.current) {
            showToast('warning', 'Please wait, submission in progress');
            return;
        }

        isSubmittingRef.current = true;
        submissionLockRef.current = true;
        setLoading(true);

        try {
            const salaryData = {
                action: 'add_salary',
                ...formData,
                username: user.username,
                shop_id: currentShop.shop_id
            };

            const response = await fetch('https://ourzestapp.online/oz_backend/employee_salaries.php', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(salaryData)
            });

            const data = await response.json();

            if (data.status === 'success') {
                showToast('success', 'Salary record added successfully!');

                // Prepare voucher data
                setVoucherData({
                    ...formData,
                    salaryid: data.salaryid,
                    employee_details: employeeDetails,
                    business_info: businessInfo,
                    shop: currentShop
                });

                setShowPayVoucher(true);

                // Reset form
                setFormData({
                    employeeid: '',
                    employee_name: '',
                    salary_month: new Date().toISOString().slice(0, 7),
                    basic_salary: 0,
                    allowances: 0,
                    deductions: 0,
                    bonus: 0,
                    overtime_hours: 0,
                    overtime_amount: 0,
                    commission_amount: 0,
                    net_salary: 0,
                    payment_method: 'cash',
                    payment_status: 'pending',
                    payment_date: new Date().toISOString().split('T')[0],
                    transaction_id: '',
                    notes: '',
                    themeColor: formData.themeColor
                });
                setEmployeeSearchTerm('');
                setSelectedEmployee(null);
                setEmployeeDetails(null);
            } else {
                showToast('error', data.message || 'Failed to add salary record');
            }
        } catch (error) {
            console.error('Error adding salary:', error);
            showToast('error', 'Network error occurred');
        } finally {
            setTimeout(() => {
                isSubmittingRef.current = false;
                submissionLockRef.current = false;
                setLoading(false);
            }, 1000);
        }
    };

    const handleSwitchShop = async (shop) => {
        try {
            const success = await switchShop(shop.shop_id, shop.shop_name, shop.shop_code);
            if (success) {
                showToast('success', 'Shop switched successfully');
                fetchEmployees();
            } else {
                showToast('error', 'Failed to switch shop');
            }
        } catch (error) {
            console.error('Error switching shop:', error);
            showToast('error', 'Failed to switch shop');
        }
    };

    // Load available shops
    useEffect(() => {
        const loadAvailableShops = async () => {
            if (!user) return;
            setLoadingShops(true);
            try {
                const shopsData = await getShopsForSwitch();
                setAvailableShops(shopsData.shops || []);
            } catch (error) {
                console.error('Error loading shops:', error);
                setAvailableShops([]);
            } finally {
                setLoadingShops(false);
            }
        };

        loadAvailableShops();
    }, [user, getShopsForSwitch]);

    // Generate and download PDF voucher
    const generatePayVoucher = async (salaryData, businessInfo, employeeDetails, shop) => {
        try {
            const primaryColor = formData.themeColor || '#6366f1';

            // Create PDF
            const pdf = new jsPDF({
                orientation: 'portrait',
                unit: 'mm',
                format: 'a4'
            });

            const pageWidth = pdf.internal.pageSize.getWidth();
            const pageHeight = pdf.internal.pageSize.getHeight();

            // Helper function for hex to rgba
            function hexToRgba(hex, alpha = 1) {
                const r = parseInt(hex.slice(1, 3), 16);
                const g = parseInt(hex.slice(3, 5), 16);
                const b = parseInt(hex.slice(5, 7), 16);
                return `rgba(${r}, ${g}, ${b}, ${alpha})`;
            }

            // Create HTML content for voucher
            const voucherHTML = `
                <div style="width: 800px; padding: 30px; font-family: 'Inter', sans-serif; background: white; border-radius: 8px; box-shadow: 0 4px 12px rgba(0,0,0,0.1);">
                    <!-- Header with Business Info -->
                    <div style="display: flex; justify-content: space-between; align-items: center; border-bottom: 2px solid ${primaryColor}; padding-bottom: 20px; margin-bottom: 20px;">
                        <div style="display: flex; align-items: center; gap: 15px;">
                            ${businessInfo.bussinesslogo ? `
                                <img src="${businessInfo.bussinesslogo}" alt="Logo" style="height: 60px; width: auto; border-radius: 8px;">
                            ` : ''}
                            <div>
                                <h1 style="font-size: 28px; font-weight: 700; color: ${primaryColor}; margin: 0;">${businessInfo.businessname || 'Business Name'}</h1>
                                <p style="font-size: 14px; color: #666; margin: 5px 0 0;">${businessInfo.businessaddress || 'Address'}</p>
                                <p style="font-size: 14px; color: #666; margin: 2px 0;">${businessInfo.phonenumber || ''} | ${businessInfo.email || ''}</p>
                            </div>
                        </div>
                        <div style="background: ${primaryColor}; color: white; padding: 10px 20px; border-radius: 6px;">
                            <h2 style="font-size: 20px; margin: 0;">PAY VOUCHER</h2>
                            <p style="font-size: 14px; margin: 5px 0 0;">#${salaryData.salaryid || 'NEW'}</p>
                        </div>
                    </div>

                    <!-- Shop Info -->
                    <div style="background: #f8f9fa; padding: 15px; border-radius: 6px; margin-bottom: 20px;">
                        <p style="font-size: 14px; margin: 0; color: #444;">
                            <strong>Shop:</strong> ${shop?.shop_name || ''} (${shop?.shop_code || ''})
                        </p>
                    </div>

                    <!-- Employee Details -->
                    <div style="margin-bottom: 25px;">
                        <h3 style="font-size: 18px; color: ${primaryColor}; margin-bottom: 15px;">Employee Details</h3>
                        <div style="display: grid; grid-template-columns: repeat(2, 1fr); gap: 15px;">
                            <div>
                                <p style="font-size: 14px; color: #666; margin: 0 0 5px;">Employee Name</p>
                                <p style="font-size: 16px; font-weight: 600; margin: 0;">${employeeDetails?.employee_name || salaryData.employee_name}</p>
                            </div>
                            <div>
                                <p style="font-size: 14px; color: #666; margin: 0 0 5px;">Employee ID</p>
                                <p style="font-size: 16px; font-weight: 600; margin: 0;">#${employeeDetails?.employeeid || salaryData.employeeid}</p>
                            </div>
                            <div>
                                <p style="font-size: 14px; color: #666; margin: 0 0 5px;">Position</p>
                                <p style="font-size: 16px; font-weight: 600; margin: 0;">${employeeDetails?.position || '-'}</p>
                            </div>
                            <div>
                                <p style="font-size: 14px; color: #666; margin: 0 0 5px;">Department</p>
                                <p style="font-size: 16px; font-weight: 600; margin: 0;">${employeeDetails?.department || '-'}</p>
                            </div>
                            <div>
                                <p style="font-size: 14px; color: #666; margin: 0 0 5px;">Phone</p>
                                <p style="font-size: 16px; font-weight: 600; margin: 0;">${employeeDetails?.phone || '-'}</p>
                            </div>
                            <div>
                                <p style="font-size: 14px; color: #666; margin: 0 0 5px;">Email</p>
                                <p style="font-size: 16px; font-weight: 600; margin: 0;">${employeeDetails?.email || '-'}</p>
                            </div>
                        </div>
                    </div>

                    <!-- Salary Details -->
                    <div style="margin-bottom: 25px;">
                        <h3 style="font-size: 18px; color: ${primaryColor}; margin-bottom: 15px;">Salary Details</h3>
                        <div style="display: grid; grid-template-columns: repeat(2, 1fr); gap: 15px;">
                            <div>
                                <p style="font-size: 14px; color: #666; margin: 0 0 5px;">Salary Month</p>
                                <p style="font-size: 16px; font-weight: 600; margin: 0;">${salaryData.salary_month}</p>
                            </div>
                            <div>
                                <p style="font-size: 14px; color: #666; margin: 0 0 5px;">Payment Date</p>
                                <p style="font-size: 16px; font-weight: 600; margin: 0;">${salaryData.payment_date}</p>
                            </div>
                        </div>
                    </div>

                    <!-- Salary Breakdown Table -->
                    <div style="margin-bottom: 25px;">
                        <h3 style="font-size: 18px; color: ${primaryColor}; margin-bottom: 15px;">Salary Breakdown</h3>
                        <table style="width: 100%; border-collapse: collapse;">
                            <thead>
                                <tr style="background: ${primaryColor}; color: white;">
                                    <th style="padding: 10px; text-align: left;">Description</th>
                                    <th style="padding: 10px; text-align: right;">Amount (${shop?.currency || '$'})</th>
                                </tr>
                            </thead>
                            <tbody>
                                ${employeeDetails?.salary_type === 'fixed' ? `
                                    <tr>
                                        <td style="padding: 10px; border-bottom: 1px solid #eee;">Basic Salary</td>
                                        <td style="padding: 10px; border-bottom: 1px solid #eee; text-align: right;">${parseFloat(salaryData.basic_salary || employeeDetails.base_salary || 0).toFixed(2)}</td>
                                    </tr>
                                    <tr>
                                        <td style="padding: 10px; border-bottom: 1px solid #eee;">Allowances</td>
                                        <td style="padding: 10px; border-bottom: 1px solid #eee; text-align: right;">${parseFloat(salaryData.allowances || 0).toFixed(2)}</td>
                                    </tr>
                                    <tr>
                                        <td style="padding: 10px; border-bottom: 1px solid #eee;">Bonus</td>
                                        <td style="padding: 10px; border-bottom: 1px solid #eee; text-align: right;">${parseFloat(salaryData.bonus || 0).toFixed(2)}</td>
                                    </tr>
                                    <tr>
                                        <td style="padding: 10px; border-bottom: 1px solid #eee;">Commission</td>
                                        <td style="padding: 10px; border-bottom: 1px solid #eee; text-align: right;">${parseFloat(salaryData.commission_amount || 0).toFixed(2)}</td>
                                    </tr>
                                    <tr style="color: #e53e3e;">
                                        <td style="padding: 10px; border-bottom: 1px solid #eee;">Deductions</td>
                                        <td style="padding: 10px; border-bottom: 1px solid #eee; text-align: right;">-${parseFloat(salaryData.deductions || 0).toFixed(2)}</td>
                                    </tr>
                                ` : employeeDetails?.salary_type === 'hourly' ? `
                                    <tr>
                                        <td style="padding: 10px; border-bottom: 1px solid #eee;">Hourly Rate</td>
                                        <td style="padding: 10px; border-bottom: 1px solid #eee; text-align: right;">${employeeDetails.hourly_rate}</td>
                                    </tr>
                                    <tr>
                                        <td style="padding: 10px; border-bottom: 1px solid #eee;">Overtime Hours</td>
                                        <td style="padding: 10px; border-bottom: 1px solid #eee; text-align: right;">${salaryData.overtime_hours || 0}</td>
                                    </tr>
                                    <tr>
                                        <td style="padding: 10px; border-bottom: 1px solid #eee;">Overtime Amount</td>
                                        <td style="padding: 10px; border-bottom: 1px solid #eee; text-align: right;">${parseFloat(salaryData.overtime_amount || 0).toFixed(2)}</td>
                                    </tr>
                                ` : `
                                    <tr>
                                        <td style="padding: 10px; border-bottom: 1px solid #eee;">Commission Amount</td>
                                        <td style="padding: 10px; border-bottom: 1px solid #eee; text-align: right;">${parseFloat(salaryData.commission_amount || 0).toFixed(2)}</td>
                                    </tr>
                                `}
                                <tr style="background: #f8f9fa; font-weight: 700;">
                                    <td style="padding: 15px 10px; font-size: 16px;">NET SALARY</td>
                                    <td style="padding: 15px 10px; text-align: right; font-size: 18px; color: ${primaryColor};">${shop?.currency || '$'}${parseFloat(salaryData.net_salary || 0).toFixed(2)}</td>
                                </tr>
                            </tbody>
                        </table>
                    </div>

                    <!-- Payment Details -->
                    <div style="margin-bottom: 25px;">
                        <h3 style="font-size: 18px; color: ${primaryColor}; margin-bottom: 15px;">Payment Details</h3>
                        <div style="display: grid; grid-template-columns: repeat(2, 1fr); gap: 15px;">
                            <div>
                                <p style="font-size: 14px; color: #666; margin: 0 0 5px;">Payment Method</p>
                                <p style="font-size: 16px; font-weight: 600; margin: 0; text-transform: capitalize;">${salaryData.payment_method}</p>
                            </div>
                            <div>
                                <p style="font-size: 14px; color: #666; margin: 0 0 5px;">Payment Status</p>
                                <p style="font-size: 16px; font-weight: 600; margin: 0; text-transform: capitalize; color: ${salaryData.payment_status === 'paid' ? '#10b981' : salaryData.payment_status === 'pending' ? '#f59e0b' : '#ef4444'};">${salaryData.payment_status}</p>
                            </div>
                            ${salaryData.transaction_id ? `
                                <div>
                                    <p style="font-size: 14px; color: #666; margin: 0 0 5px;">Transaction ID</p>
                                    <p style="font-size: 16px; font-weight: 600; margin: 0;">${salaryData.transaction_id}</p>
                                </div>
                            ` : ''}
                        </div>
                    </div>

                    <!-- Bank Details (if available) -->
                    ${employeeDetails?.bank_name ? `
                        <div style="margin-bottom: 25px;">
                            <h3 style="font-size: 18px; color: ${primaryColor}; margin-bottom: 15px;">Bank Details</h3>
                            <div style="display: grid; grid-template-columns: repeat(3, 1fr); gap: 15px;">
                                <div>
                                    <p style="font-size: 14px; color: #666; margin: 0 0 5px;">Bank Name</p>
                                    <p style="font-size: 16px; font-weight: 600; margin: 0;">${employeeDetails.bank_name}</p>
                                </div>
                                <div>
                                    <p style="font-size: 14px; color: #666; margin: 0 0 5px;">Account Number</p>
                                    <p style="font-size: 16px; font-weight: 600; margin: 0;">${employeeDetails.account_number}</p>
                                </div>
                                <div>
                                    <p style="font-size: 14px; color: #666; margin: 0 0 5px;">IFSC Code</p>
                                    <p style="font-size: 16px; font-weight: 600; margin: 0;">${employeeDetails.ifsc_code}</p>
                                </div>
                            </div>
                        </div>
                    ` : ''}

                    <!-- Notes -->
                    ${salaryData.notes ? `
                        <div style="margin-bottom: 20px;">
                            <p style="font-size: 14px; color: #666; margin: 0 0 5px;">Notes</p>
                            <p style="font-size: 14px; margin: 0; font-style: italic;">${salaryData.notes}</p>
                        </div>
                    ` : ''}

                    <!-- Signatures -->
                    <div style="display: flex; justify-content: space-between; margin-top: 40px;">
                        <div>
                            <p style="font-size: 14px; color: #666; margin-bottom: 5px;">Employee Signature</p>
                            <div style="width: 200px; height: 1px; background: #000; margin: 20px 0 5px;"></div>
                            <p style="font-size: 14px; margin: 0;">${employeeDetails?.employee_name || salaryData.employee_name}</p>
                        </div>
                        <div>
                            <p style="font-size: 14px; color: #666; margin-bottom: 5px;">Authorized Signature</p>
                            <div style="width: 200px; height: 1px; background: #000; margin: 20px 0 5px;"></div>
                            <p style="font-size: 14px; margin: 0;">${businessInfo.fullname || 'Authorized Person'}</p>
                        </div>
                    </div>

                    <!-- Footer -->
                    <div style="margin-top: 30px; padding-top: 20px; border-top: 1px solid #eee; text-align: center; font-size: 12px; color: #999;">
                        <p>This is a computer generated voucher and does not require a physical signature.</p>
                        <p>Generated on ${new Date().toLocaleDateString()} at ${new Date().toLocaleTimeString()}</p>
                    </div>
                </div>
            `;

            // Create container
            const pdfContainer = document.createElement('div');
            pdfContainer.style.position = 'absolute';
            pdfContainer.style.left = '-9999px';
            pdfContainer.style.top = '0';
            pdfContainer.style.width = '800px';
            pdfContainer.innerHTML = voucherHTML;
            document.body.appendChild(pdfContainer);

            // Convert to canvas
            const canvas = await html2canvas(pdfContainer, {
                scale: 2,
                useCORS: true,
                backgroundColor: '#ffffff',
                logging: false
            });

            // Remove container
            document.body.removeChild(pdfContainer);

            // Add to PDF
            const imgWidth = pageWidth - 20;
            const imgHeight = (canvas.height * imgWidth) / canvas.width;

            pdf.addImage(canvas.toDataURL('image/png'), 'PNG', 10, 10, imgWidth, Math.min(imgHeight, pageHeight - 20));

            // Save PDF
            pdf.save(`Pay_Voucher_${employeeDetails?.employee_name || 'Employee'}_${salaryData.salary_month}.pdf`);

            showToast('success', 'Pay voucher downloaded successfully!');

        } catch (error) {
            console.error('Error generating pay voucher:', error);
            showToast('error', 'Error generating pay voucher');
        }
    };

    // Render shop indicator
    const renderShopIndicator = () => {
        if (!currentShop) {
            return (
                <div className="shop-indicator-modern no-shop">
                    <div className="shop-badge">
                        <FontAwesomeIcon icon={faExclamationTriangle} />
                        <span className="shop-warning">No shop selected</span>
                    </div>
                    <button
                        className="btn-modern btn-switch-shop"
                        onClick={() => setShowShopSelector(true)}
                    >
                        <FontAwesomeIcon icon={faExchangeAlt} />
                        <span>Switch Shop</span>
                    </button>
                </div>
            );
        }
        return null;
    };

    return (
        <div className={`salary-form-independent ${isRTL ? 'rtl' : ''}`}>
            {/* Toast Notification */}
            <ToastNotification
                toast={toast}
                setToast={setToast}
                translations={{}}
            />

            {/* Shop Selector Modal */}
            <ShopSelectorModal
                isOpen={showShopSelector}
                onClose={() => setShowShopSelector(false)}
                shops={availableShops}
                currentShop={currentShop}
                onSelectShop={handleSwitchShop}
                translations={{}}
                loading={loadingShops}
            />

            {/* Pay Voucher Modal */}
            {showPayVoucher && voucherData && (
                <div className="modal-overlay" onClick={() => setShowPayVoucher(false)}>
                    <div className="modal-content pay-voucher-modal" onClick={(e) => e.stopPropagation()}>
                        <div className="modal-header">
                            <h3>Pay Voucher Generated</h3>
                            <button className="modal-close" onClick={() => setShowPayVoucher(false)}>
                                <FontAwesomeIcon icon={faTimes} />
                            </button>
                        </div>
                        <div className="modal-body">
                            <div className="voucher-actions">
                                <button
                                    className="btn-modern btn-primary-modern"
                                    onClick={() => {
                                        generatePayVoucher(
                                            voucherData,
                                            businessInfo,
                                            employeeDetails,
                                            currentShop
                                        );
                                        setShowPayVoucher(false);
                                    }}
                                >
                                    <FontAwesomeIcon icon={faDownload} />
                                    Download Pay Voucher PDF
                                </button>
                                <button
                                    className="btn-modern btn-secondary-modern"
                                    onClick={() => setShowPayVoucher(false)}
                                >
                                    <FontAwesomeIcon icon={faTimes} />
                                    Close
                                </button>
                            </div>
                            <div className="voucher-preview">
                                <p>Preview of Pay Voucher</p>
                                <div className="voucher-preview-content">
                                    <p><strong>Employee:</strong> {voucherData.employee_name}</p>
                                    <p><strong>Month:</strong> {voucherData.salary_month}</p>
                                    <p><strong>Net Salary:</strong> {currentShop?.currency || '$'}{voucherData.net_salary}</p>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* Form Header */}
            <div className="form-header-modern">
                <div className="header-content">
                    <div className="header-icon">
                        <FontAwesomeIcon icon={faMoneyBillWave} />
                    </div>
                    <div className="header-text">
                        <h1>Add Employee Salary</h1>
                        <p>Fill in the salary details below</p>
                    </div>
                </div>

                {/* Shop Indicator */}
                {renderShopIndicator()}
            </div>

            {/* Form */}
            <form ref={formRef} onSubmit={handleSubmit} className="modern-form salary-form">
                {/* Employee Selection */}
                <div className="form-section">
                    <h2 className="section-title">
                        <FontAwesomeIcon icon={faUser} />
                        Select Employee
                    </h2>
                    <div className="form-grid-modern">
                        <div className={`form-group-modern ${errors.employee ? 'has-error' : ''}`} ref={employeeDropdownRef}>
                            <div className="input-container">
                                <FontAwesomeIcon icon={faSearch} className="input-icon" />
                                <input
                                    type="text"
                                    value={employeeSearchTerm}
                                    onChange={(e) => {
                                        setEmployeeSearchTerm(e.target.value);
                                        setShowEmployeeDropdown(true);
                                    }}
                                    onFocus={() => setShowEmployeeDropdown(true)}
                                    placeholder="Search employee by name, ID or phone..."
                                    disabled={loading || isSubmittingRef.current}
                                    autoComplete="off"
                                />
                                <label className="input-label">
                                    Employee
                                    <span className="required">*</span>
                                </label>
                            </div>

                            {/* Employee Dropdown */}
                            {showEmployeeDropdown && (
                                <div className="employee-dropdown">
                                    {filteredEmployees.length > 0 ? (
                                        filteredEmployees.map(emp => (
                                            <div
                                                key={emp.employeeid}
                                                className={`employee-option ${selectedEmployee?.employeeid === emp.employeeid ? 'selected' : ''}`}
                                                onClick={() => selectEmployee(emp)}
                                            >
                                                <div className="employee-option-name">
                                                    <FontAwesomeIcon icon={faUser} />
                                                    {emp.employee_name}
                                                </div>
                                                <div className="employee-option-details">
                                                    <span>ID: {emp.employeeid}</span>
                                                    <span>{emp.position || 'No position'}</span>
                                                    <span>{emp.phone || 'No phone'}</span>
                                                </div>
                                            </div>
                                        ))
                                    ) : (
                                        <div className="employee-dropdown empty">
                                            <FontAwesomeIcon icon={faExclamationTriangle} />
                                            No employees found
                                        </div>
                                    )}
                                </div>
                            )}
                            {errors.employee && (
                                <div className="error-message-modern">
                                    <FontAwesomeIcon icon={faExclamationTriangle} />
                                    {errors.employee}
                                </div>
                            )}
                        </div>
                    </div>
                </div>

                {/* Salary Period */}
                <div className="form-section">
                    <h2 className="section-title">
                        <FontAwesomeIcon icon={faCalendar} />
                        Salary Period
                    </h2>
                    <div className="form-grid-modern">
                        <div className={`form-group-modern ${errors.salary_month ? 'has-error' : ''}`}>
                            <div className="input-container">
                                <FontAwesomeIcon icon={faCalendar} className="input-icon" />
                                <input
                                    type="month"
                                    id="salary_month"
                                    name="salary_month"
                                    value={formData.salary_month}
                                    onChange={handleChange}
                                    disabled={loading || isSubmittingRef.current}
                                />
                                <label htmlFor="salary_month" className="input-label">
                                    Salary Month
                                    <span className="required">*</span>
                                </label>
                            </div>
                            {errors.salary_month && (
                                <div className="error-message-modern">
                                    <FontAwesomeIcon icon={faExclamationTriangle} />
                                    {errors.salary_month}
                                </div>
                            )}
                        </div>
                    </div>
                </div>

                {/* Salary Details based on employee type */}
                {employeeDetails && (
                    <>
                        {employeeDetails.salary_type === 'fixed' && (
                            <div className="form-section">
                                <h2 className="section-title">
                                    <FontAwesomeIcon icon={faMoneyBillWave} />
                                    Fixed Salary Details
                                </h2>
                                <div className="form-grid-modern">
                                    <div className="form-group-modern">
                                        <div className="input-container">
                                            <FontAwesomeIcon icon={faMoneyBillWave} className="input-icon" />
                                            <input
                                                type="number"
                                                id="basic_salary"
                                                name="basic_salary"
                                                value={formData.basic_salary || employeeDetails.base_salary || ''}
                                                onChange={handleChange}
                                                placeholder="Basic Salary"
                                                disabled={loading || isSubmittingRef.current}
                                                step="0.01"
                                                min="0"
                                            />
                                            <label htmlFor="basic_salary" className="input-label">
                                                Basic Salary
                                            </label>
                                        </div>
                                    </div>

                                    <div className="form-group-modern">
                                        <div className="input-container">
                                            <FontAwesomeIcon icon={faPlusCircle} className="input-icon" />
                                            <input
                                                type="number"
                                                id="allowances"
                                                name="allowances"
                                                value={formData.allowances}
                                                onChange={handleChange}
                                                placeholder="Allowances"
                                                disabled={loading || isSubmittingRef.current}
                                                step="0.01"
                                                min="0"
                                            />
                                            <label htmlFor="allowances" className="input-label">
                                                Allowances
                                            </label>
                                        </div>
                                    </div>

                                    <div className="form-group-modern">
                                        <div className="input-container">
                                            <FontAwesomeIcon icon={faPlusCircle} className="input-icon" />
                                            <input
                                                type="number"
                                                id="bonus"
                                                name="bonus"
                                                value={formData.bonus}
                                                onChange={handleChange}
                                                placeholder="Bonus"
                                                disabled={loading || isSubmittingRef.current}
                                                step="0.01"
                                                min="0"
                                            />
                                            <label htmlFor="bonus" className="input-label">
                                                Bonus
                                            </label>
                                        </div>
                                    </div>

                                    <div className="form-group-modern">
                                        <div className="input-container">
                                            <FontAwesomeIcon icon={faPercentage} className="input-icon" />
                                            <input
                                                type="number"
                                                id="commission_amount"
                                                name="commission_amount"
                                                value={formData.commission_amount}
                                                onChange={handleChange}
                                                placeholder="Commission Amount"
                                                disabled={loading || isSubmittingRef.current}
                                                step="0.01"
                                                min="0"
                                            />
                                            <label htmlFor="commission_amount" className="input-label">
                                                Commission
                                            </label>
                                        </div>
                                    </div>

                                    <div className="form-group-modern">
                                        <div className="input-container">
                                            <FontAwesomeIcon icon={faTimes} className="input-icon" />
                                            <input
                                                type="number"
                                                id="deductions"
                                                name="deductions"
                                                value={formData.deductions}
                                                onChange={handleChange}
                                                placeholder="Deductions"
                                                disabled={loading || isSubmittingRef.current}
                                                step="0.01"
                                                min="0"
                                            />
                                            <label htmlFor="deductions" className="input-label">
                                                Deductions
                                            </label>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        )}

                        {employeeDetails.salary_type === 'hourly' && (
                            <div className="form-section">
                                <h2 className="section-title">
                                    <FontAwesomeIcon icon={faClock} />
                                    Hourly Salary Details
                                </h2>
                                <div className="form-grid-modern">
                                    <div className="form-group-modern">
                                        <div className="input-container">
                                            <FontAwesomeIcon icon={faClock} className="input-icon" />
                                            <input
                                                type="number"
                                                id="overtime_hours"
                                                name="overtime_hours"
                                                value={formData.overtime_hours}
                                                onChange={handleChange}
                                                placeholder="Overtime Hours"
                                                disabled={loading || isSubmittingRef.current}
                                                step="0.5"
                                                min="0"
                                            />
                                            <label htmlFor="overtime_hours" className="input-label">
                                                Overtime Hours
                                            </label>
                                        </div>
                                    </div>
                                </div>
                                <div className="info-box">
                                    <p>Hourly Rate: {currentShop?.currency || '$'}{employeeDetails.hourly_rate}/hour</p>
                                    <p>Regular Hours: 176 hours/month (8 hours × 22 days)</p>
                                </div>
                            </div>
                        )}

                        {employeeDetails.salary_type === 'commission' && (
                            <div className="form-section">
                                <h2 className="section-title">
                                    <FontAwesomeIcon icon={faPercentage} />
                                    Commission Details
                                </h2>
                                <div className="form-grid-modern">
                                    <div className="form-group-modern">
                                        <div className="input-container">
                                            <FontAwesomeIcon icon={faPercentage} className="input-icon" />
                                            <input
                                                type="number"
                                                id="commission_amount"
                                                name="commission_amount"
                                                value={formData.commission_amount}
                                                onChange={handleChange}
                                                placeholder="Commission Amount"
                                                disabled={loading || isSubmittingRef.current}
                                                step="0.01"
                                                min="0"
                                            />
                                            <label htmlFor="commission_amount" className="input-label">
                                                Commission Amount
                                            </label>
                                        </div>
                                    </div>
                                    <div className="info-box">
                                        <p>Commission Rate: {employeeDetails.commission_rate}%</p>
                                    </div>
                                </div>
                            </div>
                        )}

                        {/* Payment Details */}
                        <div className="form-section">
                            <h2 className="section-title">
                                <FontAwesomeIcon icon={faMoneyBillWave} />
                                Payment Details
                            </h2>
                            <div className="form-grid-modern">
                                <div className={`form-group-modern ${errors.net_salary ? 'has-error' : ''}`}>
                                    <div className="input-container">
                                        <FontAwesomeIcon icon={faMoneyBillWave} className="input-icon" />
                                        <input
                                            type="number"
                                            id="net_salary"
                                            name="net_salary"
                                            value={formData.net_salary}
                                            onChange={handleChange}
                                            placeholder="Net Salary"
                                            disabled={loading || isSubmittingRef.current}
                                            step="0.01"
                                            min="0"
                                            readOnly
                                        />
                                        <label htmlFor="net_salary" className="input-label">
                                            Net Salary
                                            <span className="required">*</span>
                                        </label>
                                    </div>
                                    {errors.net_salary && (
                                        <div className="error-message-modern">
                                            <FontAwesomeIcon icon={faExclamationTriangle} />
                                            {errors.net_salary}
                                        </div>
                                    )}
                                </div>

                                <div className="form-group-modern">
                                    <div className="input-container">
                                        <FontAwesomeIcon icon={faMoneyBillWave} className="input-icon" />
                                        <select
                                            id="payment_method"
                                            name="payment_method"
                                            value={formData.payment_method}
                                            onChange={handleChange}
                                            disabled={loading || isSubmittingRef.current}
                                        >
                                            {paymentMethodOptions.map(option => (
                                                <option key={option.value} value={option.value}>
                                                    {option.label}
                                                </option>
                                            ))}
                                        </select>
                                        <label htmlFor="payment_method" className="input-label">
                                            Payment Method
                                        </label>
                                    </div>
                                </div>

                                <div className="form-group-modern">
                                    <div className="input-container">
                                        <FontAwesomeIcon icon={faCheckCircle} className="input-icon" />
                                        <select
                                            id="payment_status"
                                            name="payment_status"
                                            value={formData.payment_status}
                                            onChange={handleChange}
                                            disabled={loading || isSubmittingRef.current}
                                        >
                                            {paymentStatusOptions.map(option => (
                                                <option key={option.value} value={option.value}>
                                                    {option.label}
                                                </option>
                                            ))}
                                        </select>
                                        <label htmlFor="payment_status" className="input-label">
                                            Payment Status
                                        </label>
                                    </div>
                                </div>

                                <div className="form-group-modern">
                                    <div className="input-container">
                                        <FontAwesomeIcon icon={faCalendar} className="input-icon" />
                                        <input
                                            type="date"
                                            id="payment_date"
                                            name="payment_date"
                                            value={formData.payment_date}
                                            onChange={handleChange}
                                            disabled={loading || isSubmittingRef.current}
                                        />
                                        <label htmlFor="payment_date" className="input-label">
                                            Payment Date
                                        </label>
                                    </div>
                                </div>

                                <div className="form-group-modern">
                                    <div className="input-container">
                                        <FontAwesomeIcon icon={faFileInvoice} className="input-icon" />
                                        <input
                                            type="text"
                                            id="transaction_id"
                                            name="transaction_id"
                                            value={formData.transaction_id}
                                            onChange={handleChange}
                                            placeholder="Transaction ID (if applicable)"
                                            disabled={loading || isSubmittingRef.current}
                                        />
                                        <label htmlFor="transaction_id" className="input-label">
                                            Transaction ID
                                        </label>
                                    </div>
                                </div>

                                <div className="form-group-modern full-width">
                                    <div className="input-container">
                                        <FontAwesomeIcon icon={faFileInvoice} className="input-icon" />
                                        <textarea
                                            id="notes"
                                            name="notes"
                                            value={formData.notes}
                                            onChange={handleChange}
                                            rows="3"
                                            placeholder="Additional notes"
                                            disabled={loading || isSubmittingRef.current}
                                        />
                                        <label htmlFor="notes" className="input-label">
                                            Notes
                                        </label>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </>
                )}

                {/* Form Actions */}
                <div className="form-actions-modern">
                    <button
                        type="button"
                        className="btn-modern btn-secondary-modern"
                        onClick={() => {
                            setFormData({
                                ...formData,
                                employeeid: '',
                                employee_name: '',
                                basic_salary: 0,
                                allowances: 0,
                                deductions: 0,
                                bonus: 0,
                                overtime_hours: 0,
                                overtime_amount: 0,
                                commission_amount: 0,
                                net_salary: 0,
                                transaction_id: '',
                                notes: ''
                            });
                            setEmployeeSearchTerm('');
                            setSelectedEmployee(null);
                            setEmployeeDetails(null);
                            setErrors({});
                            lastSubmissionRef.current = null;
                        }}
                        disabled={loading || isSubmittingRef.current}
                    >
                        <FontAwesomeIcon icon={faTimes} />
                        <span>Clear</span>
                    </button>
                    <button
                        type="submit"
                        className="btn-modern btn-primary-modern"
                        disabled={loading || !currentShop || isSubmittingRef.current || submissionLockRef.current}
                    >
                        {loading || isSubmittingRef.current ? (
                            <>
                                <div className="btn-spinner"></div>
                                <span>Saving...</span>
                            </>
                        ) : (
                            <>
                                <span>Save Salary</span>
                                <FontAwesomeIcon icon={faSave} />
                            </>
                        )}
                    </button>
                </div>

                {/* Submission Status */}
                {(isSubmittingRef.current || submissionLockRef.current) && (
                    <div className="submission-status">
                        <div className="submission-status-text">
                            <FontAwesomeIcon icon={faSpinner} spin />
                            <span>Processing your submission...</span>
                        </div>
                    </div>
                )}
            </form>

            {/* Loading Overlay */}
            {loading && (
                <div className="loading-overlay">
                    <div className="loading-content">
                        <div className="loading-spinner"></div>
                        <p>Saving salary record...</p>
                        <p className="loading-subtext">Please wait while we process the salary</p>
                    </div>
                </div>
            )}
        </div>
    );
};

export default AddSalary;